import { Avatar } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import type { ColumnsType } from 'antd/es/table'
import {
  type TravelRateCardLevel,
  TravelRateCardStatus
} from 'screens/Rostering/hooks/useTravelRateCardSettings'
import styled from 'styled-components'

export const formatMinutesToHours = (minutes: number) => {
  return (minutes / 60).toFixed(1)
}

export const formatMetersToMiles = (meters: number) => {
  return (meters * 0.000621371).toFixed(1)
}

export const getOverviewColumns = (
  t: (key: string) => string,
  flagsTravelTimeAndMileageEnabled: boolean,
  siteRateCard?: {
    levelType: TravelRateCardLevel
    status: TravelRateCardStatus
  }
) => {
  const baseColumns: ColumnsType<any> = [
    {
      title: (
        <HeaderContainer>
          <HeaderText>{t('TEAM_MEMBERS')}</HeaderText>
        </HeaderContainer>
      ),
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 200,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name: string, record: any) => (
        <PersonContainer>
          <AvatarContainer>
            <Avatar url={record.picture} />
          </AvatarContainer>
          <NameText weight="bold">{name}</NameText>
        </PersonContainer>
      )
    },
    {
      title: (
        <HeaderContainer>
          <HeaderText>{t('WEEKLY_CONTRACTED_HOURS')}</HeaderText>
        </HeaderContainer>
      ),
      dataIndex: 'weeklyContractedMinutes',
      key: 'weeklyContractedMinutes',
      width: 120,
      sorter: (a, b) => a.weeklyContractedMinutes - b.weeklyContractedMinutes,
      render: (minutes: number) => (
        <FlexContainer>
          <Text>{formatMinutesToHours(minutes)}</Text>
        </FlexContainer>
      )
    },
    {
      title: (
        <HeaderContainer>
          <HeaderText>{t('HOURS_ASSIGNED')}</HeaderText>
        </HeaderContainer>
      ),
      dataIndex: 'assignedMinutes',
      key: 'assignedMinutes',
      width: 120,
      sorter: (a, b) => a.assignedMinutes - b.assignedMinutes,
      render: (minutes: number) => (
        <FlexContainer>
          <Text>{formatMinutesToHours(minutes)}</Text>
        </FlexContainer>
      )
    },
    {
      title: (
        <HeaderContainer>
          <HeaderText>{t('HOURS_WORKED')}</HeaderText>
        </HeaderContainer>
      ),
      dataIndex: 'workedMinutes',
      key: 'workedMinutes',
      width: 120,
      sorter: (a, b) => a.workedMinutes - b.workedMinutes,
      render: (minutes: number) => (
        <FlexContainer>
          <Text>{formatMinutesToHours(minutes)}</Text>
        </FlexContainer>
      )
    },
    {
      title: (
        <HeaderContainer>
          <HeaderText>{t('UNDERUTILISED_HOURS')}</HeaderText>
        </HeaderContainer>
      ),
      dataIndex: 'underutilisedMinutes',
      key: 'underutilisedMinutes',
      width: 120,
      sorter: (a, b) => a.underutilisedMinutes - b.underutilisedMinutes,
      render: (minutes: number) => (
        <FlexContainer>
          <Text>{formatMinutesToHours(minutes)}</Text>
        </FlexContainer>
      )
    },
    {
      title: (
        <HeaderContainer>
          <HeaderText>{t('ABSENT_DAYS')}</HeaderText>
        </HeaderContainer>
      ),
      dataIndex: 'absentDays',
      key: 'absentDays',
      width: 120,
      sorter: (a, b) => a.absentDays - b.absentDays,
      render: (days: number) => (
        <FlexContainer>
          <Text>{days}</Text>
        </FlexContainer>
      )
    },
    {
      title: (
        <HeaderContainer>
          <HeaderText>{t('OVERTIME_HOURS')}</HeaderText>
        </HeaderContainer>
      ),
      dataIndex: 'overtimeMinutes',
      key: 'overtimeMinutes',
      width: 120,
      sorter: (a, b) => a.overtimeMinutes - b.overtimeMinutes,
      render: (minutes: number) => (
        <FlexContainer>
          <Text>{formatMinutesToHours(minutes)}</Text>
        </FlexContainer>
      )
    },
    {
      title: (
        <HeaderContainer>
          <HeaderText>{t('EXCEPTIONS')}</HeaderText>
        </HeaderContainer>
      ),
      dataIndex: 'exceptionCount',
      key: 'exceptionCount',
      width: 120,
      sorter: (a, b) => a.exceptionCount - b.exceptionCount,
      render: (count: number) => (
        <FlexContainer>
          <Text>{count}</Text>
        </FlexContainer>
      )
    }
  ]

  const travelColumns: ColumnsType<any> = [
    {
      title: (
        <HeaderContainer>
          <HeaderText>{t('TRAVEL_TIME_MINS')}</HeaderText>
        </HeaderContainer>
      ),
      dataIndex: 'travelMinutes',
      key: 'travelMinutes',
      width: 120,
      sorter: (a, b) => a.travelMinutes - b.travelMinutes,
      render: (minutes: number) => (
        <FlexContainer>
          <Text>{formatMinutesToHours(minutes)}</Text>
        </FlexContainer>
      )
    },
    {
      title: (
        <HeaderContainer>
          <HeaderText>{t('MILEAGE_MILES')}</HeaderText>
        </HeaderContainer>
      ),
      dataIndex: 'distanceMeters',
      key: 'distanceMeters',
      width: 120,
      sorter: (a, b) => a.distanceMeters - b.distanceMeters,
      render: (meters: number) => (
        <FlexContainer>
          <Text>{formatMetersToMiles(meters)}</Text>
        </FlexContainer>
      )
    },
    {
      title: (
        <HeaderContainer>
          <HeaderText>{t('TOTAL_GAPS_BETWEEN_VISITS')}</HeaderText>
        </HeaderContainer>
      ),
      dataIndex: 'totalVisitGaps',
      key: 'totalVisitGaps',
      width: 120,
      sorter: (a, b) => a.totalVisitGaps - b.totalVisitGaps,
      render: (minutes: number) => (
        <FlexContainer>
          <Text>{minutes}</Text>
        </FlexContainer>
      )
    }
  ]

  return [
    ...baseColumns,
    ...(flagsTravelTimeAndMileageEnabled &&
    siteRateCard?.status === TravelRateCardStatus.ENABLED
      ? travelColumns
      : [])
  ]
}

const PersonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 56px;
`

const HeaderText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color1};
  font-weight: 500;
`

const NameText = styled(Text)`
  word-break: break-word;
  white-space: normal;
`

const HeaderContainer = styled.div<{ width?: number }>`
  width: ${({ width }) => (width ? `${width}px` : 'auto')};
`

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const AvatarContainer = styled.div`
  width: 32px;
  height: 32px;
`
